import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CalendarDays, CheckCircle2, Clock, Loader2, MapPin } from "lucide-react";
import { fmtD } from "../lib/date";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SLOTS = ["上午 AM", "下午 PM", "傍晚 Evening"];

export default function SurveyConfirm() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState("view");
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState(SLOTS[0]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [doneMsg, setDoneMsg] = useState("");

  useEffect(() => {
    axios.get(`${BACKEND}/api/public/survey-confirm/${token}`)
      .then(({ data }) => {
        setInfo(data);
        if (data.ownerConfirmed) setDoneMsg("您已确认此勘测时间 · You have confirmed this appointment");
        else if (data.ownerProposal) setDoneMsg(`您已提出新时间 ${fmtD(data.ownerProposal.date)} ${data.ownerProposal.timeSlot || ""}，项目顾问会尽快与您确认 · Proposal received`);
      })
      .catch((e) => setErr(e?.detailText || "链接无效或已过期 Invalid or expired link"));
  }, [token]);

  const act = async (payload) => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await axios.post(`${BACKEND}/api/public/survey-confirm/${token}`, payload);
      setDoneMsg(data.confirmed
        ? "已确认勘测时间，我们的团队将准时到访！ · Confirmed — see you then!"
        : "已收到您的新时间，项目顾问会尽快与您确认最终安排 · We received your proposed time.");
    } catch (e) { setErr(e?.detailText || "提交失败，请稍后重试 Failed, please retry"); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="survey-confirm-page">
      <div className="bg-white rounded-2xl shadow-xl border border-slate-100 max-w-md w-full p-8">
        <div className="text-center mb-6">
          <p className="text-2xl font-black text-slate-900 tracking-wide">ALIGCUS <span className="text-red-600">阿里建筑</span></p>
          <p className="text-xs text-slate-400 mt-1">现场勘测时间确认 · Site Survey Confirmation</p>
        </div>
        {err && !info && <p className="text-sm text-red-600 text-center py-6" data-testid="sc-error">{err}</p>}
        {info && (
          <>
            <div className="rounded-xl bg-slate-50 border border-slate-100 p-4 space-y-2 text-sm">
              <p className="flex items-center gap-2 text-slate-700"><MapPin className="h-4 w-4 text-red-500 shrink-0" />{info.address || "-"}</p>
              <p className="flex items-center gap-2 text-slate-700"><CalendarDays className="h-4 w-4 text-indigo-500 shrink-0" /><b data-testid="sc-when">{fmtD(info.date)} {info.timeSlot || ""}</b></p>
              <p className="text-[11px] text-slate-400">尊敬的 {info.clientName}，我们已为您安排现场勘测，请确认时间或提出更方便的时间。</p>
            </div>
            {doneMsg ? (
              <div className="mt-6 text-center" data-testid="sc-done">
                <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto" />
                <p className="text-sm text-slate-700 mt-2 font-medium">{doneMsg}</p>
              </div>
            ) : mode === "view" ? (
              <div className="flex flex-col gap-2 mt-6">
                <button data-testid="sc-confirm-btn" disabled={busy} onClick={() => act({ action: "confirm" })}
                  className="w-full py-2.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}确认此时间 Confirm
                </button>
                <button data-testid="sc-propose-btn" disabled={busy} onClick={() => setMode("propose")}
                  className="w-full py-2.5 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 font-semibold text-sm inline-flex items-center justify-center gap-2">
                  <Clock className="h-4 w-4" />提出新时间 Propose new time
                </button>
              </div>
            ) : (
              <div className="mt-6 space-y-3">
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="sc-date"
                  className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2" />
                <select value={slot} onChange={(e) => setSlot(e.target.value)} data-testid="sc-slot"
                  className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 bg-white">
                  {SLOTS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={300} data-testid="sc-note"
                  placeholder="备注（可选）Note (optional)" className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2" />
                {err && <p className="text-xs text-red-600" data-testid="sc-error2">{err}</p>}
                <div className="flex gap-2">
                  <button data-testid="sc-submit" disabled={busy || !date} onClick={() => act({ action: "propose", date, timeSlot: slot, note })}
                    className="flex-1 py-2.5 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm disabled:opacity-50">
                    {busy ? "提交中…" : "提交新时间 Submit"}
                  </button>
                  <button data-testid="sc-back" onClick={() => setMode("view")} className="px-4 rounded-full border border-slate-200 text-slate-500 text-sm">返回</button>
                </div>
              </div>
            )}
          </>
        )}
        <p className="text-center text-[10px] text-slate-300 mt-6">ALIGCUS Construction · 626-803-8000</p>
      </div>
    </div>
  );
}
